import React, { useMemo, useState } from 'react'
import {
  BundleStyled,
  BundlesContent,
  BundlesStepTitleCurrency,
  BundlesStepFlexRow,
  BundlesPlansAndCurrentPlanWrapper,
} from 'Features/pricing/tabs/Bundles/Bundle.styled'
import { Plan } from 'Features/pricing/types/Plan'
import { PricingTabHeader } from 'Features/pricing/components/PricingTabHeader/PricingTabHeader'
import { useTranslation } from 'react-i18next'
import { GeneralSelect, Stack, useResponsive } from '@clickcease/cc-client-components-lib'

import { BundlePlanBox } from 'Features/pricing/components/BundlePlanBox/BundlePlanBox'
import { BundlesFeaturesWrapper } from './BundlesFeaturesWrapper/BundlesFeaturesWrapper'
import { CURRENCIES, CurrenciesTypes } from 'Types/Currencies'
import { PLAN_TYPES, PlanTypes } from 'Types/PlansTypes'
import Currencies from 'Assets/lists/currencies'
import { SelectOption } from 'Types/SelectOption'
import { BundleSteps } from 'Features/pricing/tabs/Bundles/BundleSteps/BundleSteps'
import { wrapperCss, wrapperMobileCss } from '../helper'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { PRODUCT_TYPES } from 'Types/ProductTypes'
import { usePricingEndOfTrial } from 'Hooks/split/usePricingEndOfTrial'
import { usePaymentsFlowSplit } from 'Hooks/split/usePaymentsFlow'

type BundleProps = {
  paidMarketingPlans: Array<Plan>
  botMitigationPlans: Array<Plan>
  reactivation?: boolean
  billingCycleMonths?: number
}

export const Bundle = ({
  paidMarketingPlans,
  botMitigationPlans,
  reactivation,
  billingCycleMonths,
}: BundleProps) => {
  const { isMobile } = useResponsive()
  const { t } = useTranslation('pricing')
  const [pmNumOfVisits, setPmNumOfVisits] = useState<number | number[]>(100)
  const [bmNumOfVisits, setBmNumOfVisits] = useState<number | number[]>(200)
  const [pmSelectedPlan, setPmSelectedPlan] = useState<PlanTypes>(PLAN_TYPES.CORE)
  const [bmSelectedPlan, setBmSelectedPlan] = useState<PlanTypes>(PLAN_TYPES.CORE)
  const [currency, setCurrency] = useState<CurrenciesTypes>(CURRENCIES.USD)
  const { data: currentPlanDetails, isFetched } = useGetPlansDetails()
  const pricingEndOfTrial = usePricingEndOfTrial()
  const paymentFlow = usePaymentsFlowSplit()
  const NoCcPricingEndOfTrial = paymentFlow === 'NO_CC' && pricingEndOfTrial

  const isFetchedPlans = useMemo(() => {
    if (!NoCcPricingEndOfTrial) {
      return true
    } else {
      return isFetched
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched])

  useMemo(() => {
    if (currentPlanDetails?.plans) {
      const pmKey =
        currentPlanDetails?.plans[0]?.product.toLowerCase() === PRODUCT_TYPES.PAID_MARKETING ? 0 : 1
      const bmKey =
        currentPlanDetails?.plans[0]?.product.toLowerCase() === PRODUCT_TYPES.BOT_ZAPPING ? 0 : 1

      //set pm
      setPmSelectedPlan(currentPlanDetails?.plans[pmKey].type as PlanTypes)
      setPmNumOfVisits(currentPlanDetails?.plans[pmKey].limit / 1000)

      //set bm
      setBmSelectedPlan(currentPlanDetails?.plans[bmKey].type as PlanTypes)
      setBmNumOfVisits(currentPlanDetails?.plans[bmKey].limit / 1000)
    }
  }, [currentPlanDetails])

  const paidMarketingPlansDb = useMemo(() => {
    const plans: Array<Plan> = []
    paidMarketingPlans.forEach((plan) => {
      if (plan.currency === currency) {
        plans.push(plan)
      }
    })
    return plans
  }, [paidMarketingPlans, currency])

  const botMitigationPlansDb = useMemo(() => {
    const plans: Array<Plan> = []
    botMitigationPlans.forEach((plan) => {
      if (plan.currency === currency) {
        plans.push(plan)
      }
    })
    return plans
  }, [botMitigationPlans, currency])

  return (
    <BundleStyled>
      <PricingTabHeader icon={isMobile ? undefined : <Stack />} text={t('bundles.header_text')} />
      <BundlesContent>
        <BundlesStepTitleCurrency>
          <GeneralSelect
            value={Currencies.find((item) => item.value === currency)}
            options={Currencies}
            wrapperCSS={isMobile ? wrapperMobileCss : wrapperCss}
            onChange={(item: SelectOption) => {
              setCurrency(item?.value as CurrenciesTypes)
            }}
          />
        </BundlesStepTitleCurrency>
        <BundlesPlansAndCurrentPlanWrapper>
          {isFetchedPlans && (
            <BundleSteps
              paidMarketingPlansDb={paidMarketingPlansDb}
              botMitigationPlansDb={botMitigationPlans}
              currency={currency}
              pmNumOfVisits={pmNumOfVisits as number}
              setPmNumOfVisits={setPmNumOfVisits}
              pmSelectedPlan={pmSelectedPlan}
              setPmSelectedPlan={setPmSelectedPlan}
              bmNumOfVisits={bmNumOfVisits as number}
              setBmNumOfVisits={setBmNumOfVisits}
              bmSelectedPlan={bmSelectedPlan}
              setBmSelectedPlan={setBmSelectedPlan}
            />
          )}
          <BundlesStepFlexRow>
            <BundlePlanBox
              paidMarketingPlansDb={paidMarketingPlansDb}
              botMitigationPlansDb={botMitigationPlansDb}
              paidMarketingPlan={pmSelectedPlan}
              paidMarketingVisits={pmNumOfVisits as number}
              botMitigationPlan={bmSelectedPlan}
              botMitigationVisits={bmNumOfVisits as number}
              currency={currency}
              reactivation={reactivation}
              billingCycleMonths={billingCycleMonths}
            />
          </BundlesStepFlexRow>
        </BundlesPlansAndCurrentPlanWrapper>
      </BundlesContent>
      <BundlesFeaturesWrapper />
    </BundleStyled>
  )
}
